import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { EMPTY_VALUES, errorMsg } from "../../../Apis/functions";
import { CreateInvitation } from "../../../reduxToolkit/Invitation/InvitationApi";
import { MY_APARTMENT_INVITATION_MODEL } from "../Model/Model";
import { getVisitorTypeLabel } from "../../Dashboard/Visitor/Type/Model/Model";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Invite again modal logic: the visitor of the invitation being sent again is
 * already known, so only the schedule of the new visit is asked, its form state
 * and the handlers the modal binds to the inputs and to the footer buttons.
 *
 * @param {Object} props
 * @param {boolean} props.show - Modal visibility, the form is reset every time it changes.
 * @param {Function} props.onHide - Closes the modal.
 * @param {Object} [props.itemSelected] - Visit the invitation is sent again for.
 * @param {Function} [props.setModified] - Raises the flag that reloads the invitations of the tab.
 * @returns {Object} Localization function, visitor of the invitation, form state and the modal handlers.
 */
export const InviteAgainModalApp = ({ show, onHide = () => {}, itemSelected, setModified } = {}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [emptyFields, setEmptyFields] = useState(false);
    const [invitation, setInvitation] = useState({ ...MY_APARTMENT_INVITATION_MODEL });

    // Visitor of the invitation, it is only rendered on the head of the modal
    // because the resident cannot change it, the invitation is sent to them again
    const visitorName = itemSelected?.visitorFullName || EMPTY_VALUES.DEFAULT;
    const visitorType = getVisitorTypeLabel(t, itemSelected?.visitorType);

    useEffect(() => {
        reset();
    }, [show]);

    /**
     * Leaves the form as it is opened, with the period of the visit and the
     * instructions empty, the resident writes them for the new visit.
     */
    const reset = () => {
        setEmptyFields(false);
        setInvitation({
            ...MY_APARTMENT_INVITATION_MODEL,
            name: itemSelected?.visitorName ?? '',
            lastName: itemSelected?.visitorLastName ?? '',
            email: itemSelected?.visitorEmail ?? ''
        });
    };

    /**
     * Updates the field the resident is typing on.
     *
     * @param {Object} e - Input change event.
     */
    const handleChange = (e) => {
        const { name, value } = e.target;

        setInvitation((prev) => ({ ...prev, [name]: value }));
    };

    /**
     * Closes the modal and leaves the form empty.
     */
    const handleClose = () => {
        reset();
        onHide();
    };

    /**
     * Validates the period picked and creates the invitation again. The visitor,
     * the host, the zone, the visitor type and the tolerance are the ones of the
     * invitation being sent again, only the period and the instructions are the
     * ones the resident just wrote.
     */
    const handleConfirm = () => {
        if (!invitation?.isScheduleValid()) {
            setEmptyFields(true);
            return;
        }

        setEmptyFields(false);

        const body = invitation.toRegister({
            hostId: itemSelected?.hostId,
            visitorId: itemSelected?.visitorId,
            zoneId: itemSelected?.zoneId,
            visitorTypeId: itemSelected?.visitorType?.id,
            accessToleranceMin: itemSelected?.accessToleranceMin
        });

        dispatch(CreateInvitation(body)).then(({ payload: { data: { success, code } } }) => {
            if (success !== true) {
                errorMsg(code);
                return;
            }

            toast.success(t('the_invitation_has_been_sent_again'));
            // Reloads the invitations of the tab with the one just created
            setModified?.(true);
            handleClose();
        });
    };

    return {
        t,
        emptyFields,
        invitation,
        visitorName,
        visitorType,
        handleChange,
        handleClose,
        handleConfirm
    };
};
